import { ApolloClient, gql, HttpLink, InMemoryCache } from '@apollo/client';

import { logger } from '@widget/utils/logger';

import type { NavItem, RedirectItem } from '@widget/api/types';
import type { LocaleValue } from '@widget/config/host';
import type { CallBackBlockType } from '@toolbox/components/HeaderCommon/CallBackBlock';

const storyblokApiUrl = 'https://gapi.storyblok.com/v1/api';
const query = gql`
  query getSettings($lang: String) {
    SettingsItems(starts_with: $lang) {
      items {
        id
        uuid
        lang
        content {
          widgetConfiguration
          gtmId
          gaMeasurementId
          navItems
          phone_number_label
          phone_number_text
          phone_number_target
          phone_number_icon {
            filename
            alt
          }
          login_link
          callbackCta
        }
      }
    }
  }
`;

type SettingsItem = {
  content: {
    widgetConfiguration: { widgetXId: string }[];
    gtmId: string;
    gaMeasurementId: string;
    redirectList: RedirectItem[];
    navItems: NavItem[];
    phone_number_label: string;
    phone_number_text: string;
    phone_number_target: string;
    phone_number_icon: {
      filename: string;
      alt: string;
    };
    login_link: string | null;
    callbackCta: CallBackBlockType[] | null;
  };
};

type PhoneCtaType = {
  tel: string;
  text: string;
  target: string;
};

export type THeaderSettings = {
  navItems: NavItem[] | null;
  phoneCta: PhoneCtaType | null;
  loginLink: string | null;
  callbackCta: CallBackBlockType | null;
};

type WebSettings = {
  widgetXId: string;
  gtmId: string;
  gaMeasurementId: string;
  redirectList: RedirectItem[];
  headerSettings: THeaderSettings;
};

const defaultSettings: WebSettings = {
  widgetXId: 'default',
  gtmId: '',
  gaMeasurementId: '',
  redirectList: [],
  headerSettings: {
    navItems: null,
    phoneCta: null,
    loginLink: null,
    callbackCta: null,
  },
};

export function createWebSettingsQuery(token: string) {
  const httpLink = new HttpLink({
    uri: storyblokApiUrl, // Server URL (must be absolute)
    headers: {
      token,
      version: 'published',
    },
  });
  const apolloClient = new ApolloClient({
    link: httpLink,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        nextFetchPolicy: 'cache-and-network',
      },
      query: {
        fetchPolicy: 'network-only',
      },
    },
  });

  return async function getWebSettings(locale: LocaleValue): Promise<WebSettings> {
    try {
      const resp = await apolloClient.query<{ SettingsItems: { items: SettingsItem[] } }>({
        query,
        variables: { lang: locale },
      });

      let { widgetXId, gtmId, gaMeasurementId, redirectList } = defaultSettings;
      let navItems: NavItem[] = [],
        phoneCta = null,
        loginLink = null,
        callbackCta = null;

      for (const item of resp.data?.SettingsItems?.items || []) {
        if (item.content?.widgetConfiguration?.[0]?.widgetXId) {
          widgetXId = item.content?.widgetConfiguration?.[0]?.widgetXId;
        }

        if (item.content.gtmId) {
          gtmId = item.content.gtmId;
        }

        if (item.content.gaMeasurementId) {
          gaMeasurementId = item.content.gaMeasurementId;
        }

        if (item.content.redirectList) {
          redirectList = item.content.redirectList;
        }
        if (item.content.navItems) {
          navItems = item.content.navItems;
        }
        if (
          item.content.phone_number_label &&
          item.content.phone_number_text &&
          item.content.phone_number_target
        ) {
          phoneCta = {
            tel: item.content.phone_number_label,
            text: item.content.phone_number_text,
            target: item.content.phone_number_target,
          };
        }
        if (item.content.login_link) {
          loginLink = item.content.login_link;
        }
        if (item.content.callbackCta?.length) {
          callbackCta = item.content.callbackCta[0];
        }
      }

      return {
        widgetXId,
        gtmId,
        gaMeasurementId,
        redirectList,
        headerSettings: { navItems, phoneCta, loginLink, callbackCta },
      };
    } catch (error) {
      logger.error('getWebSettings query error', error);
      return defaultSettings;
    }
  };
}
